import { FC, useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { useBreakpoint } from '@contexts/breakpoints';
import { callService } from '@api/caller';
import { SearchViewModel } from '@api/index';
import { FlexibleDiv } from '@components/formStyles';
import {
  ItemText,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon from '@components/utils/CustomIcon';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import makeBold from '@utils/makeBold';
import AutoComplete, { Option } from '@shared/AutoComplete';
import { MapPin2Line } from '@assets/icons';
import { PastSearches } from '@shared';

import {
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  ItemErrorText,
  OptionWrapper,
  SelectWrapper,
} from './styled';

export type TourTypes = SearchViewModel | null;
type PropsTypes = {
  label: string;
  onChange?: (data: TourTypes) => void;
  isSubmit?: boolean;
  resetIsSubmit: () => void;
  onClick?(): void;
  value?: TourTypes;
  editable?: boolean;
  pastTourItems?: TourTypes[];
  clearPastTourSearch?(): void;
  removePastTourSearch?(item: TourTypes): void;
};

const TourAutoComplete: FC<PropsTypes> = ({
  label,
  onChange: pOnChange = (v) => v,
  isSubmit,
  resetIsSubmit,
  onClick,
  value: initialValue,
  editable = true,
  pastTourItems = [],
  clearPastTourSearch,
  removePastTourSearch,
}) => {
  const { xs } = useBreakpoint();

  const [value, setValue] = useState<TourTypes>(null);
  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState<Array<SearchViewModel> | []>([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isTourResultsLoading, setIsTourResultsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (value === '') {
      setValue(null);
      pOnChange(null);
    }

    setIsDataFetched(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsTourResultsLoading(true);
      const tours = await callService('SearchesService', 'searchesTour', {
        s: s,
      });
      tours && setData(tours);
    } catch (error) {
      setData([]);
    } finally {
      setIsTourResultsLoading(false);
      setIsDataFetched(true);
    }
  };

  const searchTour = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue = value?.text !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchTour(inputValue);
    } else {
      setData([]);
    }
  }, [inputValue]);

  useEffect(() => {
    if (value) {
      pOnChange(value);
      setIsError(false);
    } else {
      pOnChange(null);
      setIsError(true);
    }
  }, [value]);

  const handleFocus = () => {
    setIsFocus(true);
    setIsTouched(true);
  };

  const handleBlur = () => {
    setIsFocus(false);
    setIsTouched(false);
    resetIsSubmit();
  };

  useEffect(() => {
    if (isSubmit) {
      setIsTouched(true);
    }
  }, [isSubmit]);

  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue?.text || '');
      setValue(initialValue);
    }
  }, [initialValue]);

  const selectValue = (option: SearchViewModel) => {
    setValue(option);
    pOnChange(option);
    setInputValue(option?.text || '');
  };

  const isShowItemTextError =
    isDataFetched &&
    !isTourResultsLoading &&
    inputValue.length > 2 &&
    data &&
    Array.isArray(data) &&
    !data.length;

  const showPastSearch =
    Boolean(pastTourItems?.length) && inputValue.length === 0;

  return (
    <SelectWrapper onClick={onClick}>
      <AutoComplete
        onSearch={handleSearch}
        onFocus={handleFocus}
        onBlur={handleBlur}
        allowClear
        editable={editable}
        isFocus={isFocus}
        isError={(isError && isTouched) as boolean}
        value={inputValue}
        data-testid='turInput'
        dropdownStyle={{
          minWidth: xs || xs !== undefined ? 'auto' : '400px',
        }}
        label={label}
        icon={MapPin2Line}
      >
        <>
          {showPastSearch ? (
            <>
              <Option className='pastSearchesTitleWrapper'>
                <PastSearches.Header
                  label='Son Aramalar'
                  handleClear={() => clearPastTourSearch?.()}
                />
              </Option>
              {pastTourItems?.map((item) => (
                <Option key={item?.text}>
                  <PastSearches.Item
                    label={item?.text}
                    type={item?.type}
                    onClick={() => selectValue(item as any)}
                    handleRemove={() => removePastTourSearch?.(item)}
                  />
                </Option>
              ))}
            </>
          ) : null}
          {data && Array.isArray(data) && data.length ? (
            data.map((option) => (
              <Option key={`${option.id}-${option.text}`}>
                <OptionWrapper onClick={() => selectValue(option)}>
                  <CustomIcon
                    width={24}
                    height={24}
                    fontSize={24}
                    paletteColor='grey800'
                    icon={getLocationIcon(option?.type || '')}
                  />
                  <FlexibleDiv right={17} />
                  <ItemTextWrapper>
                    <ItemText
                      dangerouslySetInnerHTML={{
                        __html: makeBold(option.text || '', inputValue),
                      }}
                    />
                  </ItemTextWrapper>
                </OptionWrapper>
              </Option>
            ))
          ) : isShowItemTextError ? (
            <Option>
              <OptionWrapper>
                <ItemErrorText>
                  {!xs || xs === undefined ? (
                    <span>Aradığınız kriterlere uygun sonuç bulunamadı.</span>
                  ) : (
                    <span>
                      Aradığınız kriterlere uygun <br />
                      sonuç bulunamadı.
                    </span>
                  )}
                </ItemErrorText>
              </OptionWrapper>
            </Option>
          ) : null}
        </>
      </AutoComplete>
      {!isFocus && isTouched && isError && (
        <ErrorMessageContainer>
          <Arrow />
          <ErrorMessage>Tur adı seçiniz</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </SelectWrapper>
  );
};

export default TourAutoComplete;
